import type { ReactElement } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import ContentMarkup from '@/components/content-markup';
import { Skeleton } from '@/components/ui/skeleton';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { registry } from '@/lib/page-designer/registry';
import type { ComponentType } from '@/components/region';

interface ContentAssetProps {
    body?: string;
    designMetadata?: ComponentDesignMetadata;
    component?: ComponentType;
    className?: string;
    regionId?: string;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately */
@Component('contentAsset', {
    name: 'Content Asset',
    description: 'Displays a content asset body with optional data binding and React component mapping',
    group: 'Content',
})
export class ContentAssetMetadata {
    @AttributeDefinition({
        id: 'body',
        name: 'Body',
        type: 'markup',
        required: true,
        description: 'HTML body — bind to a content asset body via Page Designer data binding',
    })
    body?: string;
}
/* v8 ignore stop */

function ContentAssetFallback(): ReactElement {
    return (
        <div className="space-y-4" data-testid="content-asset-fallback">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
        </div>
    );
}

/**
 * Renders HTML from a Page Designer data-bound content asset body attribute.
 */
export default function ContentAsset({ body, className }: ContentAssetProps): ReactElement | null {
    if (!body?.trim()) {
        return null;
    }

    return (
        <div data-testid="content-asset">
            <ContentMarkup content={body} className={className} />
        </div>
    );
}

export { ContentAssetFallback as fallback };

/** Sync registry entry so client hydration matches SSR (see `root.tsx`). */
export function registerContentAssetEager(targetRegistry = registry): void {
    targetRegistry.registerComponent('Content.contentAsset', ContentAsset);
}
